import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import { FilesProvider } from '../../../files/providers/files-provider';
import { CoachesProvider, CoachRow } from '../../providers/coaches-provider';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef, MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Team } from "../../../common/models/team";
import { User } from "../../../common/models/user";
import { Organization } from "../../../common/models/organization";
import { Lang } from "../../../common/models/lang";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AthletesProvider } from "../../../athletes/providers/athletes-provider";
import { LangsProvider } from "../../../common/providers/langs-provider";
import { map, take } from "rxjs/operators";
import { Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { MatOption } from "@angular/material/autocomplete";
import { MatFormField } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatSelect } from "@angular/material/select";
import { AsyncPipe, NgForOf, NgIf } from "@angular/common";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { Athlete } from "../../../common/models/athlete";

@Component({
  selector: 'coaches-athletes-form',
  providers: [],
  styleUrls: ['./athletes.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    MatDialogContent,
    MatDialogActions,
    MatOption,
    MatFormField,
    MatIcon,
    MatProgressBar,
    MatSelect,
    ReactiveFormsModule,
    MatDialogClose,
    MatDialogTitle,
    NgForOf,
    NgIf,
    MatIconButton,
    MatButton,
    MatTooltip,
    NgxMatSelectSearchModule,
    AsyncPipe
  ],
  templateUrl: './athletes.html'
})
export class CoachesAthletesFormComponent implements OnInit, AfterViewInit, OnDestroy {
  public isLoading = false;
  public formTitle = '';

  public availableTeams: Team[] = [];

  public organisation: Organization|null = null;
  public coachRow: CoachRow |null= null;
  public coachId = 0;
  public user: User = new User({});

  public langId: number = Lang.ID_DEFAULT;

  @ViewChild('multiSelect', { static: true }) multiSelect!: MatSelect;
  public availableAthletes: User[] = [];
  public athletesSelected: User[] = [];
  public athletesSelectFormControl: FormControl<User[]> = new FormControl<User[]>([]) as FormControl<User[]>;
  public athleteFilterFormControl: FormControl<string> = new FormControl<string>('') as FormControl<string>;
  public filteredAthletes: ReplaySubject<User[]> = new ReplaySubject<User[]>(1);
  protected _onDestroy = new Subject<void>();

  constructor(
    public conf: ConfigProvider,
    public currentUser: CurrentUser,
    public utils: UtilsProvider,
    public dialog: MatDialogRef<CoachesAthletesFormComponent>,
    public usersProvider: UsersProvider,
    private _toaster: MatSnackBar,
    private _title: Title,
    private _athletesProvider: AthletesProvider,
    private _router: Router,
    private _route: ActivatedRoute,
    private _coachesProvider: CoachesProvider,
    private _langsProvider: LangsProvider,
    private _translater: TranslateService,
    private _files: FilesProvider,
    @Inject(MAT_DIALOG_DATA) private _data:any
  ) {
    this.availableAthletes = _data.availableAthletes;
    this.availableTeams = _data.availableTeams;
    this.organisation = _data.organization;
    this.formTitle = this._data.formTitle;

    if (this._data.coachRow) {

      this.coachRow = this._data.coachRow;
      this.coachId = this._data.coachRow.coach.id;
      this.user = this._data.coachRow.user;
      this.langId = this._data.coachRow.user.langId;
      // get all user from coachRow.athletes.user and put them in athletesSelected
      this.athletesSelected = this._data.coachRow.athletes;
    }
  }

  public ngOnInit() {
    // Charger les coachs filtrés initialement
    this.filteredAthletes.next(this.availableAthletes.slice());

    // Écouter les changements dans le champ de recherche
    this.athleteFilterFormControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterAthletes();
      });

    // Initialiser les coachs sélectionnés si nécessaire
    if (this.athletesSelected.length > 0) {
      this.athletesSelectFormControl.setValue(this.athletesSelected);
    }
  }

  public ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  public ngAfterViewInit() {
    this.setInitialValue();
  }

  public onSaveClicked(): void {
    if (this.canSave()) {
      this.isLoading = true;
      this.save().subscribe({
        next: (result: CoachRow) => {
          this._toaster.open(this._translater.instant('Coach_saved'), '', {duration: 5000});
          this.isLoading = false;
          this.dialog.close(result);
        },
        error: (error: any) => this.onError(error)
      });
    } else {
      this.touchFields();
      this._toaster.open(this._translater.instant('Correct_form_errors'), '', {duration: 5000});
    }
  }

  public canSave(): boolean {
    return !this.athletesSelectFormControl.invalid;
  }

  private touchFields(): void {
    this.athletesSelectFormControl.markAsTouched();
  }

  private save(): Observable<CoachRow> {
    const coachRow: CoachRow = {
      user: this.coachRow?.user!,
      coach: this.coachRow?.coach!,
      athletes: [],
      athleteNames: [],
      teams: [],
      teamNames: [],
      isSuperCoach: false
    };

    if (this.athletesSelectFormControl.value.length) {
      for (let i = 0; i < this.athletesSelectFormControl.value.length; i++) {
        const athleteUser = this.utils.findIn(
          this.athletesSelectFormControl.value[i].id,
          this.availableAthletes,
          'id');
        if (athleteUser) {
          coachRow.athletes.push(athleteUser);
        }
      }
    }

    return this._coachesProvider.saveCoachAthletes(this.organisation?.id!, coachRow).pipe(
      map((row: CoachRow) => row)
    );
  }

  protected setInitialValue() {
    this.filteredAthletes
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.multiSelect.compareWith = (a: User, b: User) => a && b && a.id === b.id;
      });
  }

  protected filterAthletes() {
    if (!this.availableAthletes) {
      return;
    }

    const search = this.athleteFilterFormControl.value?.toLowerCase() || '';
    if (!search) {
      this.filteredAthletes.next(this.availableAthletes.slice());
      return;
    }

    this.filteredAthletes.next(
      this.availableAthletes.filter((athlete) =>
        `${athlete.firstName} ${athlete.lastName}`.toLowerCase().includes(search)
      )
    );
  }

  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 422) {
      if (error.error.errors && error.error.errors.email) {
        // c'est qu'on a essayé de faire valider un email test@test par exemple (c'est validé par le validator angular mais pas laravel...)
        return;
      }
    }

    this._toaster.open(this._translater.instant('Error_unknown'), '', {duration: 5000});
  }

}
